import { useEffect } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { requireGuest } from '../features/auth/guards.js';
import { AuthLangSwitcher } from '../components/auth/AuthLangSwitcher.js';

const FOCUSABLE_FIELD_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);

// Facebook/Messenger's in-app browser frequently ignores `interactive-widget=
// resizes-content` and fails to auto-scroll a focused field above the on-screen
// keyboard. `visualViewport` still reports the shrunk height reliably there, so
// nudge the focused field back into view once the keyboard finishes animating in.
export function scrollFocusedFieldIntoView(active: Element | null): void {
  if (active instanceof HTMLElement && FOCUSABLE_FIELD_TAGS.has(active.tagName)) {
    active.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

function AuthLayout() {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const onResize = () => scrollFocusedFieldIntoView(document.activeElement);
    viewport.addEventListener('resize', onResize);
    return () => viewport.removeEventListener('resize', onResize);
  }, []);

  return (
    <main className="min-h-dvh grid place-items-center px-4 relative">
      {/* Cohesive premium background for every auth page (never fragments on
          wide screens like the old per-corner radials did). */}
      <div className="bf-auth-bg" aria-hidden />
      <div className="bf-stars" aria-hidden />
      <div className="absolute top-4 right-4 z-10">
        <AuthLangSwitcher />
      </div>
      <Outlet />
    </main>
  );
}

export const Route = createFileRoute('/_auth')({
  beforeLoad: requireGuest,
  component: AuthLayout,
});
